import {
  QuestionnaireScoreBand,
  QuestionnaireScoreVisual,
} from "./IslandRegistry";

const hexToRgb = (hex: string) => {
  const cleaned = hex.replace("#", "");
  const bigint = parseInt(cleaned, 16);

  return {
    r: (bigint >> 16) & 255,
    g: (bigint >> 8) & 255,
    b: bigint & 255,
  };
};

const rgbToHex = (r: number, g: number, b: number) => {
  return `#${[r, g, b]
    .map((value) => Math.round(value).toString(16).padStart(2, "0"))
    .join("")}`;
};

const interpolateColor = (color1: string, color2: string, factor: number) => {
  const c1 = hexToRgb(color1);
  const c2 = hexToRgb(color2);

  const r = c1.r + (c2.r - c1.r) * factor;
  const g = c1.g + (c2.g - c1.g) * factor;
  const b = c1.b + (c2.b - c1.b) * factor;

  return rgbToHex(r, g, b);
};

export const getScorePercent = (
  score: number,
  scoreVisual?: QuestionnaireScoreVisual
) => {
  if (!scoreVisual) {
    return 0;
  }

  const clampedScore = Math.max(0, Math.min(score, scoreVisual.maxScore));
  return (clampedScore / scoreVisual.maxScore) * 100;
};

export const getScoreGradientFromVisual = (
  scoreVisual?: QuestionnaireScoreVisual
) => {
  if (!scoreVisual?.colorStops.length) {
    return "linear-gradient(to right, #34C759 0%, #FFCE48 27%, #FF9800 55%, #EE353F 78%, #EE353F 100%)";
  }

  const stops = [...scoreVisual.colorStops].sort(
    (left, right) => left.percent - right.percent
  );
  const direction = scoreVisual.invertBarDirection ? "to left" : "to right";

  return `linear-gradient(${direction}, ${stops
    .map(({ color, percent }) => `${color} ${Math.max(0, Math.min(percent, 100))}%`)
    .join(", ")})`;
};

export const getScoreColorFromVisual = (
  score: number,
  scoreVisual?: QuestionnaireScoreVisual
) => {
  if (!scoreVisual) {
    return "#FF5722";
  }

  const percent = getScorePercent(score, scoreVisual);
  const stops = scoreVisual.colorStops;

  for (let index = 0; index < stops.length - 1; index += 1) {
    const start = stops[index];
    const end = stops[index + 1];

    if (percent >= start.percent && percent <= end.percent) {
      const localFactor = (percent - start.percent) / (end.percent - start.percent || 1);
      return interpolateColor(start.color, end.color, localFactor);
    }
  }

  return stops[stops.length - 1]?.color ?? "#FF5722";
};

export const getScoreBand = (
  score: number,
  scoreBands?: QuestionnaireScoreBand[]
): { message: string; subMessage?: string } => {
  if (!scoreBands?.length) {
    return {
      message: `Score obtenu : ${score}`,
      subMessage: undefined,
    };
  }

  return (
    scoreBands.find((band) => score <= band.max) ??
    scoreBands[scoreBands.length - 1]
  );
};